// ============================================================================
// SelettoreSchede.jsx
// Componente riusabile per collegare schede di sicurezza a una sottocategoria
// del dizionario.
//
// Props:
//   schedeSelezionate: string[]  -- array di codici (es. ["ED120","LA040"])
//   onChange: (newCodici) => void  -- chiamato quando cambia la selezione
//   contesto?: string  -- testo descrittivo opzionale (es. "cartongesso/pareti")
// ============================================================================

import React, { useState, useEffect } from 'react';

const NAS_URL = "";

// Cache locale del manifest schede (caricato una sola volta per sessione)
let _schedeCache = null;
let _caricaInCorso = null;

async function caricaSchede(forza = false) {
  // FIX_SELETTORE_SCHEDE_REFRESH_v1: se forza=true, ignora la cache e rilegge dal server
  if (forza) { _schedeCache = null; }
  if (_schedeCache) return _schedeCache;
  if (_caricaInCorso) return _caricaInCorso;

  _caricaInCorso = (async () => {
    const token = localStorage.getItem('bertoc_jwt');
    if (!token) throw new Error('Non sei autenticato');
    const r = await fetch(`${NAS_URL}/api/schede-sicurezza/lista`, {
      headers: { 'Authorization': 'Bearer ' + token }
    });
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const data = await r.json();
    _schedeCache = data;
    return data;
  })();

  try {
    const result = await _caricaInCorso;
    return result;
  } finally {
    _caricaInCorso = null;
  }
}

// Stili base coerenti col gestionale (dark)
const COLORS = {
  bg: '#0f1117',
  surface: '#1a1d27',
  border: '#2a2e45',
  accent: '#f5a623',
  text: '#e8eaf2',
  textMuted: '#7b82a0',
  textDim: '#4a5068',
  success: '#3ecf8e',
};

export default function SelettoreSchede({ schedeSelezionate = [], onChange, contesto }) {

  const [modalAperto, setModalAperto] = useState(false);
  const [tutte, setTutte] = useState(null);
  const [errore, setErrore] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [filtroMacro, setFiltroMacro] = useState('');

  // FIX_SELETTORE_SCHEDE_REFRESH_v1: ricarica SEMPRE all'apertura (vede subito le schede nuove)
  useEffect(() => {
    if (modalAperto) {
      caricaSchede(true)
        .then(d => setTutte(d))
        .catch(e => setErrore(e.message));
    }
  }, [modalAperto]);

  // Schede selezionate enrichite con metadata (per la lista compatta)
  const [metaSelezionate, setMetaSelezionate] = useState([]);
  useEffect(() => {
    // Se abbiamo i metadata, costruisci la lista enrichita
    if (tutte && tutte.schede) {
      const meta = schedeSelezionate
        .map(cod => tutte.schede.find(s => s.codice === cod))
        .filter(Boolean);
      setMetaSelezionate(meta);
    } else if (schedeSelezionate.length > 0 && !tutte) {
      // Carica in background per avere i metadata
      caricaSchede()
        .then(d => setTutte(d))
        .catch(() => {});
    }
  }, [schedeSelezionate, tutte]);

  function toggle(codice) {
    if (schedeSelezionate.includes(codice)) {
      onChange(schedeSelezionate.filter(c => c !== codice));
    } else {
      onChange([...schedeSelezionate, codice].sort());
    }
  }

  function rimuovi(codice) {
    onChange(schedeSelezionate.filter(c => c !== codice));
  }

  // FILTRA per modal
  let schedeFiltrate = tutte?.schede || [];
  if (filtroMacro) {
    schedeFiltrate = schedeFiltrate.filter(s => s.macro_codice === filtroMacro);
  }
  if (filtro) {
    const q = filtro.toLowerCase();
    schedeFiltrate = schedeFiltrate.filter(s =>
      s.codice.toLowerCase().includes(q) ||
      (s.nome || '').toLowerCase().includes(q)
    );
  }

  // Raggruppa per macro
  const perMacro = {};
  for (const s of schedeFiltrate) {
    if (!perMacro[s.macro_codice]) perMacro[s.macro_codice] = [];
    perMacro[s.macro_codice].push(s);
  }

  return (
    <div>
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: COLORS.textDim, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
          🛡️ Schede sicurezza ({schedeSelezionate.length})
        </div>
        <button
          onClick={() => setModalAperto(true)}
          style={{
            background: 'none', border: 'none', color: COLORS.accent,
            cursor: 'pointer', fontSize: 10, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 1,
          }}>
          + Aggiungi
        </button>
      </div>

      {/* LISTA SELEZIONATE (chip compatte) */}
      {schedeSelezionate.length === 0 ? (
        <div style={{ fontSize: 11, color: COLORS.textDim, fontStyle: 'italic' }}>
          Nessuna scheda collegata
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {schedeSelezionate.map(cod => {
            const meta = metaSelezionate.find(m => m.codice === cod);
            return (
              <div key={cod} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 6px 3px 8px', borderRadius: 6,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                fontSize: 11, color: COLORS.text,
              }} title={meta ? meta.nome : cod}>
                <span style={{ color: COLORS.accent, fontWeight: 700, fontFamily: 'monospace' }}>{cod}</span>
                {meta && (
                  <span style={{
                    color: COLORS.textMuted, maxWidth: 200,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {meta.nome.length > 35 ? meta.nome.slice(0, 35) + '…' : meta.nome}
                  </span>
                )}
                <button
                  onClick={() => rimuovi(cod)}
                  style={{
                    background: 'none', border: 'none', color: COLORS.textDim,
                    cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1,
                  }}
                  title="Rimuovi">×</button>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL */}
      {modalAperto && (
        <div onClick={() => setModalAperto(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
            padding: 20, maxWidth: 800, width: '100%', maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* HEADER MODAL */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>
                  🛡️ Schede di sicurezza
                </div>
                {contesto && (
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                    Per: <strong>{contesto}</strong>
                  </div>
                )}
                <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
                  Selezionate: {schedeSelezionate.length}{tutte && ` / ${tutte.totale} totali`}
                </div>
              </div>
              <button onClick={() => setModalAperto(false)} style={{
                background: 'none', border: 'none', color: COLORS.textMuted,
                cursor: 'pointer', fontSize: 22,
              }}>✕</button>
            </div>

            {errore && (
              <div style={{
                background: '#2a0f12', border: '1px solid #f0606044', borderRadius: 8,
                padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#f06060',
              }}>⚠️ {errore}</div>
            )}

            {!tutte && !errore && (
              <div style={{ textAlign: 'center', padding: 30, color: COLORS.textMuted, fontSize: 13 }}>
                Caricamento schede...
              </div>
            )}

            {tutte && (
              <>
                {/* FILTRI */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input
                    type="text"
                    placeholder="Cerca codice o nome..."
                    value={filtro}
                    onChange={e => setFiltro(e.target.value)}
                    style={{
                      flex: 1, padding: '8px 12px',
                      background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                      borderRadius: 8, color: COLORS.text, fontSize: 13, outline: 'none',
                    }}
                  />
                  <select
                    value={filtroMacro}
                    onChange={e => setFiltroMacro(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                      borderRadius: 8, color: COLORS.text, fontSize: 13, outline: 'none',
                      minWidth: 200,
                    }}
                  >
                    <option value="">Tutte le categorie</option>
                    {Object.entries(tutte.macro_categorie || {}).sort().map(([cod, nome]) => (
                      <option key={cod} value={cod}>{cod} — {nome}</option>
                    ))}
                  </select>
                </div>

                {/* LISTA SCHEDE */}
                <div style={{ overflowY: 'auto', flex: 1, paddingRight: 8 }}>
                  {Object.keys(perMacro).length === 0 && (
                    <div style={{ textAlign: 'center', padding: 30, color: COLORS.textMuted, fontSize: 13 }}>
                      Nessuna scheda trovata coi filtri attivi
                    </div>
                  )}
                  {Object.keys(perMacro).sort().map(macroCod => {
                    const macroNome = tutte.macro_categorie[macroCod] || macroCod;
                    return (
                      <div key={macroCod} style={{ marginBottom: 16 }}>
                        <div style={{
                          fontSize: 11, color: COLORS.accent, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6,
                          paddingBottom: 4, borderBottom: `1px solid ${COLORS.border}`,
                        }}>
                          {macroCod} — {macroNome} ({perMacro[macroCod].length})
                        </div>
                        {perMacro[macroCod].map(s => {
                          const sel = schedeSelezionate.includes(s.codice);
                          return (
                            <div
                              key={s.codice}
                              onClick={() => toggle(s.codice)}
                              style={{
                                display: 'flex', alignItems: 'flex-start', gap: 10,
                                padding: '8px 10px', borderRadius: 6, marginBottom: 3,
                                background: sel ? '#f5a62315' : 'transparent',
                                border: `1px solid ${sel ? COLORS.accent + '66' : 'transparent'}`,
                                cursor: 'pointer',
                                transition: 'all 0.1s',
                              }}>
                              <div style={{
                                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                                border: `2px solid ${sel ? COLORS.accent : COLORS.border}`,
                                background: sel ? COLORS.accent : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, color: '#1a1d27', fontWeight: 700,
                                marginTop: 1,
                              }}>
                                {sel ? '✓' : ''}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: 13, color: COLORS.text, fontWeight: 600,
                                }}>
                                  <span style={{ color: COLORS.accent, fontFamily: 'monospace', marginRight: 6 }}>
                                    {s.codice}
                                  </span>
                                  {s.nome}
                                </div>
                                <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
                                  {s.pagine} pag. · {s.file}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* FOOTER MODAL */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  paddingTop: 14, marginTop: 14, borderTop: `1px solid ${COLORS.border}`,
                }}>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                    {schedeSelezionate.length} schede selezionate
                  </div>
                  <button onClick={() => setModalAperto(false)} style={{
                    background: COLORS.accent, border: 'none', color: '#1a1d27',
                    padding: '8px 18px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                    Fatto
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
